import { z } from "zod"

import { ApiInboxAddressSchema } from "./inbox.js"

// The Connections pages: every client's QuickBooks or Xero connection in one
// read, the first-post account question, and every forward-to address in the
// firm. The API route schemas in apps/api mirror these; change both together.

export const ApiAccountingProviderSchema = z.enum(["quickbooks", "xero"])
export type ApiAccountingProvider = z.infer<typeof ApiAccountingProviderSchema>

/** Where the client's own "give us access" item stands. */
export const ApiClientAccessSchema = z.object({
  /** The client's answer: quickbooks, xero, other, none; null before one. */
  provider: z.enum(["quickbooks", "xero", "other", "none"]).nullable(),
  /**
   * not_answered: sent, no answer yet. client_marked: the client says they
   * sent the invite (LedgerBox cannot see the accept step). answered: they
   * keep books another way. done: verified, by the firm or by the connect.
   * waived: the firm let it go.
   */
  state: z.enum(["not_answered", "client_marked", "answered", "done", "waived"]),
  clientMarkedAt: z.string().datetime().nullable(),
  doneAt: z.string().datetime().nullable(),
  /** Done because the client's company connected, not by hand. */
  doneOnConnect: z.boolean(),
})
export type ApiClientAccess = z.infer<typeof ApiClientAccessSchema>

export const ApiAccountingConnectionSummarySchema = z.object({
  connectionId: z.string().uuid(),
  /** active; error means a reconnect is needed; disabled with tenant choices
   * means a Xero organization waits to be picked; revoked is disconnected. */
  status: z.enum(["active", "error", "disabled", "revoked"]),
  /** The QuickBooks company or Xero organization, when known. */
  companyName: z.string().nullable(),
  externalAccountId: z.string().nullable(),
  /** When the status last changed. */
  since: z.string().datetime(),
  /** Xero organizations the sign-in granted, waiting for a pick. */
  tenantChoices: z.array(
    z.object({ tenantId: z.string(), tenantName: z.string() })
  ),
  mapping: z
    .object({
      /** Statement accounts in the client. */
      statementAccounts: z.number().int(),
      /** Statement accounts with a bank or card account in the map. */
      matched: z.number().int(),
      offsetAccountName: z.string().nullable(),
    })
    .nullable(),
})
export type ApiAccountingConnectionSummary = z.infer<
  typeof ApiAccountingConnectionSummarySchema
>

export const ApiAccountingClientSchema = z.object({
  workspaceId: z.string().uuid(),
  name: z.string(),
  /** False for the firm's own books. */
  isClient: z.boolean(),
  access: ApiClientAccessSchema.nullable(),
  quickbooks: ApiAccountingConnectionSummarySchema.nullable(),
  xero: ApiAccountingConnectionSummarySchema.nullable(),
})
export type ApiAccountingClient = z.infer<typeof ApiAccountingClientSchema>

export const ApiAccountingClientsResponseSchema = z.object({
  configured: z.object({ quickbooks: z.boolean(), xero: z.boolean() }),
  canPost: z.boolean(),
  clients: z.array(ApiAccountingClientSchema),
})
export type ApiAccountingClientsResponse = z.infer<
  typeof ApiAccountingClientsResponseSchema
>

/** The statement account a document posts from, and where it posts now. */
export const ApiStatementAccountResponseSchema = z.object({
  account: z
    .object({
      id: z.string().uuid(),
      label: z.string(),
      last4: z.string().nullable(),
      accountType: z.string(),
    })
    .nullable(),
  /** The QuickBooks or Xero account it posts to; null asks the question. */
  mapped: z.object({ accountId: z.string(), accountName: z.string() }).nullable(),
})
export type ApiStatementAccountResponse = z.infer<
  typeof ApiStatementAccountResponseSchema
>

export const ApiStatementAccountBodySchema = z.object({
  /** The LedgerBox statement account. */
  statementAccountId: z.string().uuid(),
  /** The QuickBooks or Xero bank or card account it posts to. */
  accountId: z.string().min(1).max(50),
})
export type ApiStatementAccountBody = z.infer<typeof ApiStatementAccountBodySchema>

export const ApiStatementAccountSavedSchema = z.object({
  postingMappingId: z.string().uuid(),
  mapped: z.object({ accountId: z.string(), accountName: z.string() }),
})
export type ApiStatementAccountSaved = z.infer<
  typeof ApiStatementAccountSavedSchema
>

/** One client's forward-to address, with how many forwards came lately. */
export const ApiFirmInboxSchema = z.object({
  workspaceId: z.string().uuid(),
  clientName: z.string(),
  /** False for the firm's own books, which keep an address too. */
  isClient: z.boolean(),
  inbox: ApiInboxAddressSchema,
  forwards: z.object({ total: z.number().int(), accepted: z.number().int() }),
})
export type ApiFirmInbox = z.infer<typeof ApiFirmInboxSchema>

export const ApiFirmInboxesResponseSchema = z.object({
  /** False when this environment cannot receive forwarded mail. */
  configured: z.boolean(),
  /** Days the forward counts cover. */
  windowDays: z.number().int(),
  inboxes: z.array(ApiFirmInboxSchema),
})
export type ApiFirmInboxesResponse = z.infer<typeof ApiFirmInboxesResponseSchema>
